import React, { useState, useEffect } from 'react';
import { withRouter, useParams } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import PropTypes from 'prop-types';
import Select from 'react-select';
import io from 'socket.io-client';
import config from '../../config';

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Import API helper
import httprequester from '../../lib/httprequester';

const UpdateWebinar = ({ history }) => {
  const { id } = useParams();

  // Status options for react-select
  const statusOptions = [
    { value: 'UPCOMING', label: 'Upcoming' },
    { value: 'ONGOING', label: 'Ongoing' },
    { value: 'COMPLETED', label: 'Completed' }
  ];

  // State management
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    desc: '',
    date: '',
    quota: '',
    status: 'UPCOMING',
    skp: '',
    Point: '',
    live_url: '',
    video_url: '',
    link_lms: '',
    thumbnail: ''
  });
  const [currentThumbnail, setCurrentThumbnail] = useState('');
  const [speakers, setSpeakers] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [selectedSpecialists, setSelectedSpecialists] = useState([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [fetchingSpecialists, setFetchingSpecialists] = useState(true);
  const [fetchingSpeakers, setFetchingSpeakers] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Speaker creation modal state
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);
  const [newSpeaker, setNewSpeaker] = useState({ name: '' });
  const [creatingSpeaker, setCreatingSpeaker] = useState(false);

  // WebSocket and file upload state
  const [socket, setSocket] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [currentUploadId, setCurrentUploadId] = useState(null);
  const [uploadPhase, setUploadPhase] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');

  // Enhanced upload state
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadTimeRemaining, setUploadTimeRemaining] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [connectionHealth, setConnectionHealth] = useState('good');
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(3);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [lastProgressTime, setLastProgressTime] = useState(null);
  const [canResume, setCanResume] = useState(false);
  const [resumeData, setResumeData] = useState(null);

  // Fetch webinar details on component mount
  useEffect(() => {
    if (id) {
      fetchWebinarDetails();
    }
    fetchSpecialists();
    fetchSpeakers();
  }, [id]);

  // Initialize WebSocket connection with enhanced features
  useEffect(() => {
    const newSocket = io(config.websocketURL, {
      path: '/websocket/websocket',
      timeout: 120000, // 2 minutes timeout for large uploads
      forceNew: true,
      transports: ['websocket', 'polling'], // Fallback to polling if websocket fails
      reconnection: true,
      reconnectionAttempts: 10, // More retry attempts
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000, // Longer max delay
      maxReconnectionAttempts: 10,
      // Enhanced connection options for stability
      upgrade: true,
      rememberUpgrade: true,
      // Increase buffer sizes for large uploads
      maxHttpBufferSize: 1e8, // 100MB buffer
      // Better ping/pong handling
      pingTimeout: 60000,
      pingInterval: 25000
    });

    // Connection health monitoring
    let healthCheckInterval;
    let lastPingTime = Date.now();

    // Connection event handlers
    newSocket.on('connect', () => {

      setUploadError(''); // Clear any previous errors
      setConnectionHealth('good');
      setRetryCount(0);

      // Auto-resume upload if we were disconnected during upload
      if (canResume && resumeData && !isUploading) {
        setTimeout(() => {
          resumeUpload();
        }, 1000); // Small delay to ensure connection is stable
      }

      // Start health monitoring
      healthCheckInterval = setInterval(() => {
        const now = Date.now();
        const timeSinceLastPing = now - lastPingTime;

        if (timeSinceLastPing > 30000) { // 30 seconds without ping
          setConnectionHealth('poor');
        } else if (timeSinceLastPing > 15000) { // 15 seconds
          setConnectionHealth('fair');
        } else {
          setConnectionHealth('good');
        }
      }, 5000);
    });

    newSocket.on('disconnect', (reason) => {

      clearInterval(healthCheckInterval);
      setConnectionHealth('disconnected');

      if (isUploading) {

        // Store resume data for potential recovery
        if (selectedVideoFile && currentUploadId) {
          setResumeData({
            file: selectedVideoFile,
            uploadId: currentUploadId,
            uploadedBytes: uploadedBytes,
            totalBytes: totalBytes,
            progress: uploadProgress,
            disconnectReason: reason,
            timestamp: Date.now()
          });
          setCanResume(true);
        }

        // Different messages based on disconnect reason
        if (reason === 'transport error' || reason === 'transport close') {
          setUploadError('Network connection lost during upload. The system will attempt to reconnect automatically.');
        } else if (reason === 'ping timeout') {
          setUploadError('Connection timeout during upload. Please check your network connection.');
        } else {
          setUploadError('Connection lost during upload. You can resume when reconnected.');
        }

        setIsUploading(false);
        setUploadPhase('disconnected');
        setUploadMessage('Connection lost - attempting to reconnect...');
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      setConnectionHealth('error');
      setRetryCount(prev => prev + 1);

      if (retryCount >= maxRetries) {
        setUploadError('Failed to connect to upload server after multiple attempts. Please refresh the page.');
      } else {
        setUploadError(`Connection failed. Retrying... (${retryCount + 1}/${maxRetries})`);
      }
    });

    // Enhanced ping/pong handling for connection health
    newSocket.on('ping', () => {
      lastPingTime = Date.now();
    });

    newSocket.on('pong', () => {
      lastPingTime = Date.now();
    });

    // Enhanced upload progress events with speed calculation
    newSocket.on('upload-progress', (data) => {
      const now = Date.now();
      const timeElapsed = uploadStartTime ? (now - uploadStartTime) / 1000 : 0;

      setUploadProgress(data.progress);
      setUploadPhase(data.phase || 'uploading');
      setUploadMessage(data.message || 'Uploading...');

      // Calculate upload speed and time remaining
      if (data.uploadedBytes && data.totalBytes && timeElapsed > 0) {
        const bytesPerSecond = data.uploadedBytes / timeElapsed;
        const remainingBytes = data.totalBytes - data.uploadedBytes;
        const timeRemaining = remainingBytes / bytesPerSecond;

        setUploadSpeed(bytesPerSecond);
        setUploadTimeRemaining(timeRemaining);
        setUploadedBytes(data.uploadedBytes);
        setTotalBytes(data.totalBytes);
        setLastProgressTime(now);
      }
    });

    newSocket.on('upload-start', (data) => {
      setIsUploading(true);
      setUploadError('');
      setUploadPhase(data.phase || 'initializing');
      setUploadMessage(data.message || 'Starting upload...');
      setUploadStartTime(Date.now());
      setUploadedBytes(0);
      setTotalBytes(data.size || 0);
      setUploadSpeed(0);
      setUploadTimeRemaining(0);
    });

    newSocket.on('chunk-received', (data) => {
      setUploadProgress(data.progress);
      setUploadPhase(data.phase || 'receiving');
      setUploadMessage(data.message || 'Receiving chunks...');
    });

    newSocket.on('upload-complete', (data) => {
      setIsUploading(false);
      setUploadProgress(100);
      setUploadPhase('completed');
      setUploadMessage('Upload completed successfully!');
      setUploadedVideoUrl(data.url);
      setFormData(prev => ({
        ...prev,
        video_url: data.url || ''
      }));
      setSuccess(`Video file uploaded successfully!`);
      setCurrentUploadId(null);

      // Reset progress after a delay
      setTimeout(() => {
        setUploadProgress(0);
        setUploadPhase('');
        setUploadMessage('');
      }, 2000);
    });

    newSocket.on('upload-error', (data) => {
      console.error('Upload error:', data.error);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadPhase('error');
      setUploadMessage(`Upload failed: ${data.error}`);
      setUploadError(data.error);
      setError(`Video upload failed: ${data.error}`);
      setCurrentUploadId(null);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // Fetch specialists from API
  const fetchSpecialists = async () => {
    setFetchingSpecialists(true);
    try {
      const response = await httprequester.getAsync('webinar/cms/specialists');
      if (response.data.data) {
        setSpecialists(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching specialists:', err);
    } finally {
      setFetchingSpecialists(false);
    }
  };

  // Fetch speakers from API
  const fetchSpeakers = async () => {
    setFetchingSpeakers(true);
    try {
      const response = await httprequester.getAsync('webinar/cms/speakers');
      if (response.data.data) {
        setSpeakers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching speakers:', err);
    } finally {
      setFetchingSpeakers(false);
    }
  };

  // Create new speaker
  const createSpeaker = async () => {
    if (!newSpeaker.name.trim()) {
      setError('Speaker name is required');
      return;
    }

    setCreatingSpeaker(true);
    try {
      const response = await httprequester.postAsync('webinar/cms/speakers', {
        name: newSpeaker.name.trim()
      });

      if (response.data && response.data.message) {
        setSuccess(response.data.message);
        setNewSpeaker({ name: '' });
        setShowSpeakerModal(false);
        // Fetch speakers again to include the new one
        await fetchSpeakers();
      } else {
        setError('Failed to create speaker');
      }
    } catch (err) {
      console.error('Error creating speaker:', err);
      setError('Failed to create speaker. Please try again.');
    } finally {
      setCreatingSpeaker(false);
    }
  };

  // Handle video file selection
  const handleVideoFileSelect = (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm'];

    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid video file (MP4, AVI, MOV, WMV, FLV, WebM)');
      return;
    }

    // Validate file size (500MB limit for better stability)
    const maxSize = 500 * 1024 * 1024; // 500MB limit
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

    if (file.size > maxSize) {
      setError('File size exceeds 500MB limit. Please compress your video or use a smaller file.');
      return;
    }

    // Check if file is too small (might be corrupted)
    if (file.size < 1024) { // Less than 1KB
      setError('File appears to be too small or corrupted. Please select a valid video file.');
      return;
    }

    setSelectedVideoFile(file);
    setUploadError('');
    setUploadedVideoUrl('');
    setUploadPhase('');
    setUploadMessage('');
  };

  // Enhanced helper function to convert File to chunks with adaptive sizing
  const fileToChunks = (file, baseChunkSize = 1024 * 1024) => {

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const arrayBuffer = reader.result;
        const buffer = Buffer.from(arrayBuffer);

        // Adaptive chunk sizing based on file size and connection health
        let chunkSize = baseChunkSize;
        const fileSizeMB = file.size / (1024 * 1024);

        if (fileSizeMB > 500) {
          chunkSize = 2 * 1024 * 1024; // 2MB for very large files
        } else if (fileSizeMB > 100) {
          chunkSize = 1.5 * 1024 * 1024; // 1.5MB for large files
        } else if (fileSizeMB < 10) {
          chunkSize = 512 * 1024; // 512KB for small files
        }

        // Adjust chunk size based on connection health
        if (connectionHealth === 'poor') {
          chunkSize = Math.min(chunkSize, 512 * 1024); // Smaller chunks for poor connection
        } else if (connectionHealth === 'fair') {
          chunkSize = Math.min(chunkSize, 1024 * 1024); // Standard chunks for fair connection
        }

        // Split buffer into chunks
        const chunks = [];
        for (let i = 0; i < buffer.length; i += chunkSize) {
          chunks.push(buffer.slice(i, i + chunkSize));
        }


        resolve({
          chunks,
          originalname: file.name,
          mimetype: file.type,
          size: file.size,
          totalChunks: chunks.length,
          chunkSize: chunkSize
        });
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  };

  // Resume upload functionality
  const resumeUpload = async () => {
    if (!resumeData || !socket?.connected) {
      setError('Cannot resume upload. Missing resume data or not connected.');
      return;
    }

    setCurrentUploadId(resumeData.uploadId);
    setUploadedBytes(resumeData.uploadedBytes);
    setTotalBytes(resumeData.totalBytes);
    setUploadProgress(resumeData.progress);
    setCanResume(false);
    setResumeData(null);

    // Continue with the upload process
    await uploadVideoFile();
  };

  // Enhanced upload video file via WebSocket with retry and resume capabilities
  const uploadVideoFile = async () => {

    if (!socket || !selectedVideoFile) {
      setError('No file selected or WebSocket not connected');
      return;
    }

    // Check if socket is connected
    if (!socket.connected) {
      setError('WebSocket not connected. Please refresh the page and try again.');
      return;
    }

    // Check connection health before starting upload
    if (connectionHealth === 'poor' && retryCount < maxRetries) {
      setUploadMessage('Waiting for better connection...');
      setTimeout(() => uploadVideoFile(), 2000);
      return;
    }

    const uploadId = resumeData?.uploadId || `video_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setCurrentUploadId(uploadId);

    try {
      // Show upload start immediately
      setIsUploading(true);
      setUploadError('');
      setUploadProgress(resumeData?.progress || 0);
      setUploadPhase('initializing');
      setUploadMessage('Preparing upload...');
      setUploadStartTime(Date.now());

      // Determine optimal chunk size based on file size and connection
      const fileSizeMB = selectedVideoFile.size / (1024 * 1024);
      let baseChunkSize = 1024 * 1024; // Default 1MB

      if (fileSizeMB > 500) {
        baseChunkSize = 2 * 1024 * 1024; // 2MB for very large files
      } else if (fileSizeMB > 100) {
        baseChunkSize = 1.5 * 1024 * 1024; // 1.5MB for large files
      } else if (fileSizeMB < 10) {
        baseChunkSize = 512 * 1024; // 512KB for small files
      }

      const fileData = await fileToChunks(selectedVideoFile, baseChunkSize);

      // Add timeout for upload with retry capability
      const uploadTimeout = setTimeout(() => {
        if (isUploading && retryCount < maxRetries) {
          setRetryCount(prev => prev + 1);
          setUploadMessage(`Upload timeout. Retrying... (${retryCount + 1}/${maxRetries})`);
          setTimeout(() => uploadVideoFile(), 1000);
        } else {
          setUploadError('Upload timeout. Please try again with a smaller file.');
          setIsUploading(false);
          setUploadProgress(0);
          setCurrentUploadId(null);
        }
      }, 600000); // 10 minutes timeout for chunked upload


      // Optimized chunked upload with adaptive delays
      if (fileData.totalChunks > 1) {

        // Calculate adaptive delay based on connection health
        let chunkDelay = 50; // Base delay in ms
        if (connectionHealth === 'poor') {
          chunkDelay = 200; // Slower for poor connection
        } else if (connectionHealth === 'fair') {
          chunkDelay = 100; // Medium delay for fair connection
        }

        // Send chunks with enhanced connection monitoring and error handling
        for (let i = 0; i < fileData.chunks.length; i++) {
          // Enhanced connection check with retry logic
          let connectionRetries = 0;
          const maxConnectionRetries = 3;

          while (!socket.connected && connectionRetries < maxConnectionRetries) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
            connectionRetries++;
          }

          if (!socket.connected) {
            // Store current progress for resume
            setResumeData({
              file: selectedVideoFile,
              uploadId: uploadId,
              uploadedBytes: uploadedBytes,
              totalBytes: totalBytes,
              progress: Math.round((i / fileData.totalChunks) * 70),
              chunkIndex: i,
              totalChunks: fileData.totalChunks,
              timestamp: Date.now()
            });
            setCanResume(true);
            throw new Error('Connection lost during upload - can be resumed');
          }

          const chunk = fileData.chunks[i];
          const chunkData = {
            chunk: chunk,
            chunkIndex: i,
            totalChunks: fileData.totalChunks,
            fileName: selectedVideoFile.name,
            uploadId: uploadId,
            fileInfo: {
              originalname: fileData.originalname,
              mimetype: fileData.mimetype,
              size: fileData.size
            }
          };


          try {
            socket.emit('upload-chunk', chunkData);

            // Update progress for this chunk
            const chunkProgress = Math.round(((i + 1) / fileData.totalChunks) * 70);
            setUploadProgress(chunkProgress);
            setUploadedBytes(Math.round((fileData.size * (i + 1)) / fileData.totalChunks));

          } catch (emitError) {
            // Store resume data and throw error
            setResumeData({
              file: selectedVideoFile,
              uploadId: uploadId,
              uploadedBytes: uploadedBytes,
              totalBytes: totalBytes,
              progress: Math.round((i / fileData.totalChunks) * 70),
              chunkIndex: i,
              totalChunks: fileData.totalChunks,
              timestamp: Date.now()
            });
            setCanResume(true);
            throw new Error(`Failed to send chunk ${i + 1}: ${emitError.message}`);
          }

          // Adaptive delay between chunks to prevent overwhelming the server
          if (i < fileData.chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, chunkDelay));
          }
        }
      } else {

        // For small files, use the original single file upload method
        const fileBuffer = Buffer.concat(fileData.chunks);
        const fileDataSingle = {
          buffer: fileBuffer,
          originalname: fileData.originalname,
          mimetype: fileData.mimetype,
          size: fileData.size
        };


        socket.emit('upload-file', {
          file: fileDataSingle,
          fileName: selectedVideoFile.name,
          uploadId: uploadId
        });
      }


      // Clear timeout when upload completes or errors
      socket.once('upload-complete', () => {
        clearTimeout(uploadTimeout);
      });
      socket.once('upload-error', () => {
        clearTimeout(uploadTimeout);
      });

    } catch (error) {
      console.error('Upload preparation error:', error);

      if (retryCount < maxRetries) {
        setRetryCount(prev => prev + 1);
        setUploadMessage(`Upload error. Retrying... (${retryCount + 1}/${maxRetries})`);
        setTimeout(() => uploadVideoFile(), 2000);
      } else {
        setError('Error preparing file for upload. Please try again.');
        setIsUploading(false);
        setUploadProgress(0);
        setCurrentUploadId(null);
      }
    }
  };

  // Cancel upload
  const cancelUpload = () => {
    if (socket && isUploading && currentUploadId) {
      socket.emit('cancel-upload', { uploadId: currentUploadId });
      setIsUploading(false);
      setUploadProgress(0);
      setUploadError('');
      setUploadPhase('');
      setUploadMessage('');
      setCurrentUploadId(null);
    }
  };

  // Fetch webinar details from API
  const fetchWebinarDetails = async () => {
    setFetching(true);
    setError('');

    try {
      const response = await httprequester.getAsync(`webinar/cms/webinars/${id}`);

      if (response.data) {
        const webinar = response.data;
        setFormData({
          title: webinar.title || '',
          slug: webinar.slug || '',
          desc: webinar.desc || '',
          date: webinar.date ? webinar.date.slice(0, 16) : '', // Format for datetime-local input
          quota: webinar.quota || '',
          status: webinar.status || 'UPCOMING',
          skp: webinar.skp || '',
          point: webinar.Point || '',
          live_url: webinar.live_url || '',
          video_url: webinar.video_url || '',
          link_lms: webinar.link_lms || '',
          thumbnail: webinar.thumbnail || ''
        });

        // Set selected specialists for the form
        if (webinar.specialists && webinar.specialists.length > 0) {
          const selected = webinar.specialists.map(specialist => ({
            value: specialist.specialist_id,
            label: specialist.specialist_name
          }));
          setSelectedSpecialists(selected);
        }

        // Set selected speakers for the form
        if (webinar.speakers && webinar.speakers.length > 0) {
          const selected = webinar.speakers.map(speaker => ({
            value: speaker.speaker_id,
            label: speaker.speaker_name || speaker.name
          }));
          setSelectedSpeakers(selected);
        }

        // Set current thumbnail
        setCurrentThumbnail(webinar.thumbnail || '');
      } else {
        setError('Failed to fetch webinar details');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Webinar not found');
      } else {
        setError('Error fetching webinar details');
      }
      console.error('Error:', err);
    } finally {
      setFetching(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value || '' // Ensure value is never undefined
    }));
  };

  // Generate slug from title
  const generateSlug = () => {
    const slug = (formData.title || '')
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');

    setFormData(prev => ({
      ...prev,
      slug: slug || ''
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.title || !formData.slug) {
        setError('Title and Slug are required fields');
        setLoading(false);
        return;
      }

      if (selectedSpecialists.length === 0) {
        setError('At least one specialist must be selected');
        setLoading(false);
        return;
      }

      if (selectedSpeakers.length === 0) {
        setError('At least one speaker must be selected');
        setLoading(false);
        return;
      }

      // Create JSON data for submission
      const submitData = {
        title: formData.title,
        slug: formData.slug,
        desc: formData.desc,
        date: formData.date,
        quota: formData.quota,
        status: formData.status,
        skp: formData.skp,
        Point: formData.Point,
        live_url: formData.live_url,
        video_url: uploadedVideoUrl || formData.video_url, // Use uploaded URL if available
        link_lms: formData.link_lms || null,
        thumbnail: formData.thumbnail,
        specialists: selectedSpecialists.map(specialist => ({
          specialist_id: specialist.value,
          specialist_name: specialist.label
        })),
        speakers: selectedSpeakers.map(speaker => ({
          speaker_id: speaker.value,
          speaker_name: speaker.label
        }))
      };

      const response = await httprequester.putAsync(`webinar/cms/webinars/${id}`, submitData);

      if (response.data && response.data.success) {
        setSuccess('Webinar updated successfully!');
        setTimeout(() => {
          history.push(`/cme-online/detail/${id}`);
        }, 2000);
      } else {
        setError(response.data?.message || 'Failed to update webinar');
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Missing required fields. Please check your input.');
      } else if (err.response?.status === 404) {
        setError('Webinar not found');
      } else if (err.response?.status === 409) {
        setError('Slug already exists. Please choose a different one.');
      } else {
        setError('Error updating webinar');
      }
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    history.push(`/cme-online/detail/${id}`);
  };

  // Clear alerts
  const clearAlert = () => setError('');
  const clearSuccess = () => setSuccess('');

  // PropTypes
  UpdateWebinar.propTypes = {
    history: PropTypes.object.isRequired
  };

  if (fetching) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 fs-5">Loading webinar details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={clearAlert}></button>
          </div>
          <button className="btn btn-secondary" onClick={handleBack}>
            Back to Details
          </button>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Edit Webinar | CME Online | Ruang Ilmiah CMS</title>
        </MetaTags>

        <div className="container-fluid">
          <Breadcrumbs
            title="CME Online"
            breadcrumbItem="Edit Webinar"
          />

          {/* Header Actions */}
          <div className="row mb-3">
            <div className="col">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Edit Webinar</h4>
                <button className="btn btn-secondary" onClick={handleBack}>
                  <i className="bx bx-arrow-back me-1"></i>
                  Back to Details
                </button>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Left Column - Form */}
              <div className="col-7">
                {/* Basic Information */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Basic Information</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="title" className="form-label">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          id="title"
                          name="title"
                          type="text"
                          className="form-control"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="Enter webinar title"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="slug" className="form-label">
                          Slug <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            id="slug"
                            name="slug"
                            type="text"
                            className="form-control"
                            value={formData.slug}
                            onChange={handleInputChange}
                            placeholder="webinar-slug"
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={generateSlug}
                            disabled={!formData.title}
                          >
                            Generate
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="desc" className="form-label">Description</label>
                    <textarea
                      id="desc"
                      name="desc"
                      className="form-control"
                      rows="4"
                      value={formData.desc}
                      onChange={handleInputChange}
                      placeholder="Enter webinar description"
                    ></textarea>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="date" className="form-label">Event Date & Time</label>
                        <input
                          id="date"
                          name="date"
                          type="datetime-local"
                          className="form-control"
                          value={formData.date}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">Status</label>
                        <Select
                          options={statusOptions}
                          value={statusOptions.find(option => option.value === formData.status)}
                          onChange={(selectedOption) => setFormData(prev => ({
                            ...prev,
                            status: selectedOption ? selectedOption.value : 'UPCOMING'
                          }))}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
                              boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                              '&:hover': {
                                borderColor: '#80bdff'
                              }
                            }),
                            singleValue: (provided, state) => ({
                              ...provided,
                              color: '#495057'
                            }),
                            placeholder: (provided, state) => ({
                              ...provided,
                              color: '#6c757d'
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isFocused ? '#e9ecef' : 'white',
                              color: '#495057',
                              '&:active': {
                                backgroundColor: '#e9ecef'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="quota" className="form-label">Quota</label>
                        <input
                          id="quota"
                          name="quota"
                          type="number"
                          className="form-control"
                          value={formData.quota}
                          onChange={handleInputChange}
                          placeholder="Enter participant quota"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="thumbnail" className="form-label">Thumbnail URL</label>
                        <input
                          id="thumbnail"
                          name="thumbnail"
                          type="url"
                          className="form-control"
                          value={formData.thumbnail}
                          onChange={handleInputChange}
                          placeholder="https://example.com/image.jpg"
                        />
                        <small className="text-muted">
                          Enter the URL of the uploaded thumbnail image
                        </small>

                        {/* Current thumbnail preview */}
                        {currentThumbnail && (
                          <div className="mt-2">
                            <small className="text-muted d-block mb-2">Current thumbnail:</small>
                            <img
                              src={currentThumbnail}
                              alt="Current thumbnail"
                              className="img-thumbnail"
                              style={{ maxWidth: '150px', maxHeight: '100px' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* CME Points */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">CME Points</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="skp" className="form-label">SKP Points</label>
                        <input
                          id="skp"
                          name="skp"
                          type="number"
                          className="form-control"
                          value={formData.skp}
                          onChange={handleInputChange}
                          placeholder="Enter SKP points"
                          min="0"
                          step="0.5"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="Point" className="form-label">CME Points</label>
                        <input
                          id="Point"
                          name="Point"
                          type="number"
                          className="form-control"
                          value={formData.point}
                          onChange={handleInputChange}
                          placeholder="Enter CME points"
                          min="0"
                          step="0.5"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* URLs */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Links & URLs</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="live_url" className="form-label">Live Meeting URL</label>
                        <input
                          id="live_url"
                          name="live_url"
                          type="url"
                          className="form-control"
                          value={formData.live_url}
                          onChange={handleInputChange}
                          placeholder="https://meet.google.com/..."
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="video_file" className="form-label">Video Recording</label>

                        {/* File Upload Section */}
                        <div className="border rounded p-3 bg-light">
                          <div className="mb-3">
                            <input
                              type="file"
                              id="video_file"
                              className="form-control"
                              accept="video/mp4,video/avi,video/mov,video/wmv,video/flv,video/webm"
                              onChange={handleVideoFileSelect}
                              disabled={isUploading}
                            />
                            <small className="text-muted">
                              Supported formats: MP4, AVI, MOV, WMV, FLV, WebM (Max: 500MB, uses chunked upload for large files)
                            </small>
                          </div>

                          {/* Selected File Info */}
                          {selectedVideoFile && (
                            <div className="mb-3 p-2 bg-white rounded border">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <small className="text-muted">Selected file:</small>
                                  <div className="fw-semibold">{selectedVideoFile.name}</div>
                                  <small className="text-muted">
                                    {(selectedVideoFile.size / (1024 * 1024)).toFixed(2)} MB
                                  </small>
                                </div>
                                {!isUploading && (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={uploadVideoFile}
                                  >
                                    <i className="bx bx-upload me-1"></i>
                                    Upload
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Enhanced Upload Progress */}
                          {isUploading && (
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                  <small className="text-muted d-block">
                                    {uploadMessage || 'Uploading...'}
                                  </small>
                                  <small className="text-muted">
                                    {uploadPhase === 'receiving' && '📦 Receiving chunks'}
                                    {uploadPhase === 'assembling' && '🔧 Assembling file'}
                                    {uploadPhase === 'preparing' && '⚙️ Preparing upload'}
                                    {uploadPhase === 'uploading' && '☁️ Uploading to cloud'}
                                    {uploadPhase === 'initializing' && '🚀 Initializing'}
                                    {uploadPhase === 'completed' && '✅ Completed'}
                                    {uploadPhase === 'error' && '❌ Error'}
                                  </small>
                                </div>
                                <div className="text-end">
                                  <small className="text-muted fw-semibold">{uploadProgress}%</small>
                                  <div className="d-flex align-items-center gap-2">
                                    {/* Connection Health Indicator */}
                                    <span className={`badge badge-sm ${connectionHealth === 'good' ? 'bg-success' :
                                      connectionHealth === 'fair' ? 'bg-warning' :
                                        connectionHealth === 'poor' ? 'bg-danger' :
                                          'bg-secondary'
                                      }`}>
                                      {connectionHealth === 'good' && '🟢'}
                                      {connectionHealth === 'fair' && '🟡'}
                                      {connectionHealth === 'poor' && '🔴'}
                                      {connectionHealth === 'disconnected' && '⚫'}
                                      {connectionHealth === 'error' && '❌'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Progress Bar */}
                              <div className="progress" style={{ height: '12px' }}>
                                <div
                                  className={`progress-bar progress-bar-striped ${uploadPhase === 'completed' ? '' : 'progress-bar-animated'
                                    } ${uploadPhase === 'error' ? 'bg-danger' :
                                      uploadPhase === 'completed' ? 'bg-success' :
                                        uploadPhase === 'uploading' ? 'bg-primary' :
                                          'bg-info'
                                    }`}
                                  role="progressbar"
                                  style={{ width: `${uploadProgress}%` }}
                                  aria-valuenow={uploadProgress}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>

                              {/* Upload Statistics */}
                              <div className="row mt-2">
                                <div className="col-6">
                                  <small className="text-muted d-block">Upload Speed</small>
                                  <small className="fw-semibold">
                                    {uploadSpeed > 0 ? `${(uploadSpeed / (1024 * 1024)).toFixed(1)} MB/s` : 'Calculating...'}
                                  </small>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Time Remaining</small>
                                  <small className="fw-semibold">
                                    {uploadTimeRemaining > 0 ?
                                      `${Math.floor(uploadTimeRemaining / 60)}:${Math.floor(uploadTimeRemaining % 60).toString().padStart(2, '0')}` :
                                      'Calculating...'
                                    }
                                  </small>
                                </div>
                              </div>

                              {/* Bytes Progress */}
                              {uploadedBytes > 0 && totalBytes > 0 && (
                                <div className="mt-1">
                                  <small className="text-muted">
                                    {(uploadedBytes / (1024 * 1024)).toFixed(1)} MB / {(totalBytes / (1024 * 1024)).toFixed(1)} MB
                                  </small>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="mt-2 d-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={cancelUpload}
                                  disabled={uploadPhase === 'completed'}
                                >
                                  <i className="bx bx-x me-1"></i>
                                  Cancel Upload
                                </button>

                                {/* Retry Button */}
                                {uploadPhase === 'error' && retryCount < maxRetries && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-warning btn-sm"
                                    onClick={uploadVideoFile}
                                  >
                                    <i className="bx bx-refresh me-1"></i>
                                    Retry ({retryCount + 1}/{maxRetries})
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Resume Upload Option */}
                          {canResume && !isUploading && (
                            <div className="alert alert-info mb-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <i className="bx bx-info-circle me-1"></i>
                                  <strong>Upload Interrupted</strong>
                                  <div className="small text-muted">
                                    You can resume your upload from where it left off.
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  onClick={resumeUpload}
                                >
                                  <i className="bx bx-play me-1"></i>
                                  Resume Upload
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Upload Error */}
                          {uploadError && (
                            <div className="alert alert-danger alert-sm mb-3">
                              <i className="bx bx-error me-1"></i>
                              {uploadError}
                            </div>
                          )}

                          {/* Uploaded File Info */}
                          {uploadedVideoUrl && (
                            <div className="alert alert-success alert-sm mb-3">
                              <i className="bx bx-check-circle me-1"></i>
                              Video uploaded successfully!
                              <div className="mt-1">
                                <a
                                  href={uploadedVideoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-success btn-sm"
                                >
                                  <i className="bx bx-play me-1"></i>
                                  View Video
                                </a>
                              </div>
                            </div>
                          )}

                          {/* Manual URL Input (Fallback) */}
                          <div className="mt-3 pt-3 border-top">
                            <label htmlFor="video_url" className="form-label">
                              <small>Or enter video URL manually:</small>
                            </label>
                            <input
                              id="video_url"
                              name="video_url"
                              type="url"
                              className="form-control form-control-sm"
                              value={formData.video_url}
                              onChange={handleInputChange}
                              placeholder="https://example.com/video.mp4"
                              disabled={isUploading}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label htmlFor="link_lms" className="form-label">Link LMS</label>
                        <input
                          id="link_lms"
                          name="link_lms"
                          type="url"
                          className="form-control"
                          value={formData.link_lms}
                          onChange={handleInputChange}
                          placeholder="https://lms.kemkes.go.id/"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Speakers & Specialists */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Speakers & Specialists</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="speakers" className="form-label">
                          Speakers <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex gap-2">
                          <div className="flex-grow-1">
                            <Select
                              isMulti
                              options={speakers.map(speaker => ({
                                value: speaker.speaker_id,
                                label: speaker.name
                              }))}
                              value={selectedSpeakers}
                              onChange={setSelectedSpeakers}
                              placeholder="Select speakers..."
                              isLoading={fetchingSpeakers}
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
                                  boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                                  '&:hover': {
                                    borderColor: '#80bdff'
                                  }
                                }),
                                multiValue: (provided) => ({
                                  ...provided,
                                  backgroundColor: '#e9ecef'
                                }),
                                multiValueLabel: (provided) => ({
                                  ...provided,
                                  color: '#495057'
                                }),
                                multiValueRemove: (provided) => ({
                                  ...provided,
                                  color: '#6c757d',
                                  '&:hover': {
                                    backgroundColor: '#dc3545',
                                    color: 'white'
                                  }
                                })
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => setShowSpeakerModal(true)}
                            title="Add new speaker"
                          >
                            <i className="bx bx-user-plus"></i>
                          </button>
                        </div>
                        <small className="text-muted">
                          Select one or more speakers for this webinar
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="specialists" className="form-label">
                          Specialists <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          options={specialists.map(specialist => ({
                            value: specialist.specialist_id,
                            label: specialist.specialist_name
                          }))}
                          value={selectedSpecialists}
                          onChange={setSelectedSpecialists}
                          placeholder="Select specialists..."
                          isLoading={fetchingSpecialists}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
                              boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                              '&:hover': {
                                borderColor: '#80bdff'
                              }
                            }),
                            multiValue: (provided) => ({
                              ...provided,
                              backgroundColor: '#e9ecef'
                            }),
                            multiValueLabel: (provided) => ({
                              ...provided,
                              color: '#495057'
                            }),
                            multiValueRemove: (provided) => ({
                              ...provided,
                              color: '#6c757d',
                              '&:hover': {
                                backgroundColor: '#dc3545',
                                color: 'white'
                              }
                            })
                          }}
                        />
                        <small className="text-muted">
                          Select one or more specialists for this webinar
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="col-5">
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Form Preview</h5>

                  <div className="mb-3">
                    <small className="text-muted">Title</small>
                    <p className="mb-0 fw-semibold fs-5">{formData.title || 'No title entered'}</p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Slug</small>
                    <p className="mb-0 text-muted">{formData.slug || 'No slug generated'}</p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Status</small>
                    <div>
                      <span className={`badge bg-${formData.status === 'UPCOMING' ? 'warning' :
                        formData.status === 'ONGOING' ? 'success' : 'secondary'
                        }`}>
                        {formData.status}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Event Date</small>
                    <p className="mb-0">
                      {formData.date ? new Date(formData.date).toLocaleString('id-ID') : 'Not set'}
                    </p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Quota</small>
                    <p className="mb-0">{formData.quota || 'Unlimited'}</p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">CME Points</small>
                    <p className="mb-0">
                      SKP: {formData.skp || 0} | CME: {formData.point || 0}
                    </p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Speakers</small>
                    <div className="mb-0">
                      {selectedSpeakers.length > 0 ? (
                        <div>
                          {selectedSpeakers.map((speaker, index) => (
                            <span key={speaker.value} className="badge bg-info me-1">
                              {speaker.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No speakers selected</span>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Specialists</small>
                    <div className="mb-0">
                      {selectedSpecialists.length > 0 ? (
                        <div>
                          {selectedSpecialists.map((specialist, index) => (
                            <span key={specialist.value} className="badge bg-success me-1">
                              {specialist.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No specialists selected</span>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Video Recording</small>
                    <p className="mb-0">
                      {uploadedVideoUrl ? (
                        <span className="badge bg-success">
                          <i className="bx bx-check me-1"></i>
                          Video Uploaded
                        </span>
                      ) : formData.video_url ? (
                        <span className="badge bg-info">
                          <i className="bx bx-link me-1"></i>
                          URL Provided
                        </span>
                      ) : (
                        <span className="text-muted">No video recording</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="card shadow-sm rounded-4 p-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Actions</h5>

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      disabled={loading || isUploading || !formData.title || !formData.slug || selectedSpecialists.length === 0 || selectedSpeakers.length === 0}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-save me-1"></i>
                          Update Webinar
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100"
                      onClick={handleBack}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <SweetAlert
          danger
          title="Error!"
          onConfirm={clearAlert}
        >
          {error}
        </SweetAlert>
      )}

      {/* Success Alert */}
      {success && (
        <SweetAlert
          success
          title="Success!"
          onConfirm={clearSuccess}
        >
          {success}
        </SweetAlert>
      )}

      {/* Speaker Creation Modal */}
      {showSpeakerModal && (
        <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 1055 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Speaker</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSpeakerModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="speakerName" className="form-label">
                    Speaker Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="speakerName"
                    value={newSpeaker.name}
                    onChange={(e) => setNewSpeaker({ name: e.target.value })}
                    placeholder="Enter speaker name"
                    disabled={creatingSpeaker}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSpeakerModal(false)}
                  disabled={creatingSpeaker}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createSpeaker}
                  disabled={creatingSpeaker || !newSpeaker.name.trim()}
                >
                  {creatingSpeaker ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating...
                    </>
                  ) : (
                    'Create Speaker'
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
        </div>
      )}
    </React.Fragment>
  );
};

export default withRouter(UpdateWebinar);
